// NEW_FILE_JUSTIFIED: New hook for CreateLeadDialog form state management — no existing equivalent
import { useReducer, useCallback, useEffect } from 'react';
import type { TLeadSource, IAILeadExtras, IMatchedService } from '@/@types/leads';

/**
 * Infer lead source from email subject keywords.
 * Returns the best matching source or 'email' as default.
 */
function inferSourceFromSubject(subject?: string): TLeadSource {
  if (!subject) return 'email';
  const lower = subject.toLowerCase();

  if (
    lower.includes('website') ||
    lower.includes('web form') ||
    lower.includes('contact form') ||
    lower.includes('landing page')
  )
    return 'website';
  if (lower.includes('facebook') || lower.includes('fb lead')) return 'facebook';
  if (lower.includes('instagram') || lower.includes('ig lead')) return 'instagram';
  if (lower.includes('tiktok') || lower.includes('tik tok')) return 'tiktok';
  if (lower.includes('whatsapp')) return 'whatsapp';
  if (lower.includes('telegram')) return 'telegram';
  if (lower.includes('referral') || lower.includes('referred by')) return 'referral';
  if (
    lower.includes('ad campaign') ||
    lower.includes('google ads') ||
    lower.includes('meta ads') ||
    lower.includes('paid')
  )
    return 'ad';
  if (lower.includes('organic') || lower.includes('seo')) return 'organic';

  return 'email';
}

// LOCAL_TYPE_JUSTIFIED: Hook-internal state types not reused outside this file
interface ICreateLeadFormState {
  name: string;
  phoneNumber: string;
  email: string;
  source: string;
  isSubmitting: boolean;
  aiExtras?: IAILeadExtras;
  matchedServices?: IMatchedService[];
}

type TFormAction =
  | { type: 'SET_FIELD'; field: string; value: string }
  | {
      type: 'RESET';
      prefillName?: string;
      prefillEmail?: string;
      prefillPhone?: string;
      defaultSource?: string;
      aiExtras?: IAILeadExtras;
      matchedServices?: IMatchedService[];
    }
  | { type: 'SET_SUBMITTING'; value: boolean };

function formReducer(state: ICreateLeadFormState, action: TFormAction): ICreateLeadFormState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'RESET':
      return {
        name: action.prefillName ?? '',
        phoneNumber: action.prefillPhone ?? '',
        email: action.prefillEmail ?? '',
        source: action.defaultSource ?? 'email',
        isSubmitting: false,
        aiExtras: action.aiExtras,
        matchedServices: action.matchedServices,
      };
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.value };
  }
}

export function useCreateLeadForm(
  prefillName?: string,
  prefillEmail?: string,
  prefillSubject?: string,
  prefillPhone?: string,
  prefillSource?: string,
  aiExtras?: IAILeadExtras,
  matchedServices?: IMatchedService[]
) {
  const defaultSource = prefillSource ?? aiExtras?.source ?? inferSourceFromSubject(prefillSubject);

  const [state, dispatch] = useReducer(formReducer, {
    name: prefillName ?? '',
    phoneNumber: prefillPhone ?? '',
    email: prefillEmail ?? '',
    source: defaultSource,
    isSubmitting: false,
    aiExtras,
    matchedServices,
  });

  // Sync form state when prefill props change (e.g. after AI extraction completes)
  useEffect(() => {
    dispatch({
      type: 'RESET',
      prefillName,
      prefillEmail,
      prefillPhone,
      defaultSource,
      aiExtras,
      matchedServices,
    });
  }, [prefillName, prefillEmail, prefillPhone, defaultSource, aiExtras, matchedServices]);

  const setField = useCallback((field: string, value: string) => {
    dispatch({ type: 'SET_FIELD', field, value });
  }, []);

  const resetForm = useCallback(() => {
    dispatch({
      type: 'RESET',
      prefillName,
      prefillEmail,
      prefillPhone,
      defaultSource,
      aiExtras,
      matchedServices,
    });
  }, [prefillName, prefillEmail, prefillPhone, defaultSource, aiExtras, matchedServices]);

  const setIsSubmitting = useCallback((value: boolean) => {
    dispatch({ type: 'SET_SUBMITTING', value });
  }, []);

  return {
    form: state,
    setField,
    resetForm,
    isSubmitting: state.isSubmitting,
    setIsSubmitting,
    aiExtras: state.aiExtras,
    matchedServices: state.matchedServices,
  };
}
